import { useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import type { HypothesisConfig, HypothesisSummary } from '../api/useHypotheses.ts';
import type { MeetingOutcomeKind } from '../api/useMoeter.ts';
import { Ikon } from '../status/Ikon.tsx';
import { tokenVar } from '../status/status.ts';
import { isoDag, leggTilDager, leggTilMaaneder, navnAv } from './lesning.ts';
import { impactTekst, langDato, malFor, UTFALL, utfallOppforing } from './moeteLesning.ts';

/**
 * Utfallet på én møterad — det samme skjemaet i kjøringen (W5, registreringen) og når
 * beslutningstakeren endrer en rad før han bekrefter (W9, «Endre»). Utfallet er fire valg i
 * statusens farge med tastene V P A I (DD-58); det som hører til utfallet står under: eier ved
 * Velg, dato ved Parker, kode ved Avvis, svaret til innsenderen med leietakerens mal, og — bare
 * med «Menneskesatte tall» på — Impact, Confidence og Effort, aldri en sum.
 *
 * Skjemaet eier ingen lagring. `onEndre` får det som ble endret; kjøringen lagrer det straks,
 * «Endre» samler det til «Lagre endringen».
 */
export interface Utfallsverdier {
  outcome?: MeetingOutcomeKind;
  ownerId?: string | null;
  revisitAt?: string | null;
  rejectReason?: string | null;
  relatedHypothesisId?: string | null;
  response?: { text: string; templateId?: string } | null;
  impact?: number | null;
  confidence?: number | null;
  effort?: number | null;
}

export const IMPACT = [3, 2, 1, 0.5, 0.25];
export const CONFIDENCE = [100, 80, 50];

/** Det et nytt utfall tar med seg: foreslått eier og malen ved Velg, en dato og malen ved Parker. */
export function forUtfall(outcome: MeetingOutcomeKind, o: { config: HypothesisConfig | undefined; innsender: string; foreslaattEier?: { id: string; displayName?: string } }): Utfallsverdier {
  if (outcome === 'velg') {
    const mal = malFor(o.config, 'velg', { eier: o.foreslaattEier ? navnAv(o.foreslaattEier) : undefined, navn: o.innsender });
    return { outcome, ...(o.foreslaattEier ? { ownerId: o.foreslaattEier.id } : {}), ...(mal ? { response: mal } : {}) };
  }
  if (outcome === 'parker') {
    const dato = leggTilMaaneder(isoDag(new Date()), 3);
    const mal = malFor(o.config, 'parker', { dato: langDato(dato), navn: o.innsender });
    return { outcome, revisitAt: dato, ...(mal ? { response: mal } : {}) };
  }
  return { outcome };
}

export function UtfallFelter({
  id,
  etikett,
  verdier,
  config,
  decider,
  innsender,
  medlemmer,
  tall,
  testetI,
  foreslaattEier,
  onEndre,
  onLagreTekst,
}: {
  id: string;
  etikett: string;
  verdier: Utfallsverdier;
  config: HypothesisConfig | undefined;
  decider: { id: string; displayName?: string };
  innsender: { id: string; displayName?: string };
  medlemmer: ReadonlyArray<{ id: string; displayName?: string }>;
  tall: boolean;
  /** Hypotesene en «Testet før»-avvisning kan peke på (konkludert eller stoppet). */
  testetI?: ReadonlyArray<Pick<HypothesisSummary, 'id' | 'number' | 'title'>>;
  foreslaattEier?: { id: string; displayName?: string };
  onEndre: (endring: Utfallsverdier) => void;
  /** Svaret og Effort lagres når feltet forlates, ikke per tast. Uten den går de til `onEndre`. */
  onLagreTekst?: (endring: Utfallsverdier) => void;
}) {
  const lagreTekst = onLagreTekst ?? onEndre;
  const radio = useRef<HTMLDivElement>(null);
  const [svar, setSvar] = useState<string | null>(null);
  const [effort, setEffort] = useState<string | null>(null);
  const svarTekst = svar ?? verdier.response?.text ?? '';
  const effortTekst = effort ?? (verdier.effort != null ? String(verdier.effort).replace('.', ',') : '');
  const i = UTFALL.indexOf(verdier.outcome ?? ('' as MeetingOutcomeKind));
  const iDag = isoDag(new Date());
  const navnInnsender = navnAv(innsender);
  const navnDecider = navnAv(decider);

  const velgUtfall = (k: MeetingOutcomeKind) => {
    if (k === verdier.outcome) return;
    setSvar(null);
    onEndre(forUtfall(k, { config, innsender: navnInnsender, foreslaattEier }));
  };

  const flytt = (e: KeyboardEvent<HTMLDivElement>) => {
    const fram = e.key === 'ArrowRight' || e.key === 'ArrowDown';
    const bak = e.key === 'ArrowLeft' || e.key === 'ArrowUp';
    if (!fram && !bak) return;
    e.preventDefault();
    e.stopPropagation();
    const til = fram ? (i + 1 + UTFALL.length) % UTFALL.length : (i - 1 + UTFALL.length) % UTFALL.length;
    velgUtfall(UTFALL[til]!);
    radio.current?.querySelectorAll<HTMLElement>('[role="radio"]')[til]?.focus();
  };

  const svarEtikett = verdier.outcome === 'avvis' ? `Begrunnelse til ${navnInnsender} — kan skrives etterpå` : `Svar til ${navnInnsender}`;

  return (
    <div className="mu">
      <span id={`${id}-utfall`} className="mu-etikett">
        {etikett}
      </span>
      <div ref={radio} role="radiogroup" aria-labelledby={`${id}-utfall`} className="mu-utfall" onKeyDown={flytt}>
        {UTFALL.map((k, n) => {
          const u = utfallOppforing(k);
          const paa = verdier.outcome === k;
          return (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={paa}
              tabIndex={paa || (i === -1 && n === 0) ? 0 : -1}
              aria-keyshortcuts={u.tast.toUpperCase()}
              title={`${u.navn} (${u.tast.toUpperCase()})`}
              className={`mu-valg mu-valg-${k}${paa ? ' mu-valg-paa' : ''}`}
              style={{ '--mu-fg': tokenVar(u.fg), '--mu-bg': tokenVar(u.bg) } as CSSProperties}
              onClick={() => velgUtfall(k)}
            >
              <Ikon navn={paa ? 'checkmark-outline' : u.ikon} />
              {u.navn}
              <kbd aria-hidden="true">{u.tast.toUpperCase()}</kbd>
            </button>
          );
        })}
      </div>

      {verdier.outcome === 'velg' ? (
        <label className="panel-felt mu-felt">
          <span>
            Eier av testen <span className="mu-hint">en annen enn {navnDecider}</span>
          </span>
          <select value={verdier.ownerId ?? ''} onChange={(e) => {
            const eier = medlemmer.find((p) => p.id === e.target.value);
            const mal = malFor(config, 'velg', { eier: eier ? navnAv(eier) : undefined, navn: navnInnsender });
            const fraMalen = verdier.response?.templateId !== undefined && (svar === null || svar === verdier.response?.text);
            setSvar(null);
            onEndre({ ownerId: e.target.value || null, ...(fraMalen && mal ? { response: mal } : {}) });
          }}>
            <option value="">Velg en eier …</option>
            {medlemmer
              .filter((p) => p.id !== decider.id)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {navnAv(p)}
                </option>
              ))}
          </select>
        </label>
      ) : null}

      {verdier.outcome === 'parker' ? (
        <label className="panel-felt mu-felt">
          <span>Parkert til</span>
          <input type="date" min={leggTilDager(iDag, 1)} max={leggTilMaaneder(iDag, 12)} value={verdier.revisitAt ?? ''} onChange={(e) => onEndre({ revisitAt: e.target.value || null })} />
        </label>
      ) : null}

      {verdier.outcome === 'avvis' ? (
        <>
          <label className="panel-felt mu-felt">
            <span>Grunn</span>
            <select
              value={verdier.rejectReason ?? ''}
              onChange={(e) => {
                const kode = e.target.value || null;
                const mal = kode ? malFor(config, 'avvis', { kode, navn: navnInnsender }) : null;
                setSvar(null);
                onEndre({ rejectReason: kode, ...(mal && !verdier.response ? { response: mal } : {}) });
              }}
            >
              <option value="">Velg en grunn …</option>
              {(config?.rejectReasonWording ?? [])
                .filter((w) => !w.hidden)
                .map((w) => (
                  <option key={w.code} value={w.code}>
                    {w.label}
                  </option>
                ))}
            </select>
          </label>
          {verdier.rejectReason === 'vurdert_for' ? (
            <label className="panel-felt mu-felt">
              <span>Testet i</span>
              <select value={verdier.relatedHypothesisId ?? ''} onChange={(e) => onEndre({ relatedHypothesisId: e.target.value || null })}>
                <option value="">Velg hypotesen …</option>
                {(testetI ?? []).map((h) => (
                  <option key={h.id} value={h.id}>
                    {`H-${h.number} · ${h.title ?? ''}`}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </>
      ) : null}

      {verdier.outcome && verdier.outcome !== 'ikke_i_dag' ? (
        <label className="panel-felt mu-felt mu-svar">
          <span>{svarEtikett}</span>
          <textarea
            rows={3}
            maxLength={500}
            value={svarTekst}
            onChange={(e) => setSvar(e.target.value)}
            onBlur={() => {
              if (svar === null || svar === (verdier.response?.text ?? '')) return;
              lagreTekst({ response: svar.trim() ? { text: svar, ...(verdier.response?.templateId ? { templateId: verdier.response.templateId } : {}) } : null });
            }}
          />
        </label>
      ) : null}

      {tall ? (
        <div role="group" aria-labelledby={`${id}-tall`} className="mu-tall">
          <span id={`${id}-tall`} className="mu-etikett">
            Tall satt i møtet
          </span>
          <Segmenter navn="Impact" verdier={IMPACT} vis={impactTekst} aktiv={verdier.impact ?? undefined} onVelg={(v) => onEndre({ impact: v })} />
          <Segmenter navn="Confidence" verdier={CONFIDENCE} vis={(v) => `${v} %`} aktiv={verdier.confidence ?? undefined} onVelg={(v) => onEndre({ confidence: v })} />
          <label className="mu-effort">
            <span className="mu-faktor">Effort</span>
            <input
              type="number"
              inputMode="decimal"
              min={0.5}
              max={52}
              step={0.5}
              aria-label="Effort i ukesverk"
              value={effortTekst.replace(',', '.')}
              onChange={(e) => setEffort(e.target.value)}
              onBlur={() => {
                if (effort === null) return;
                const n = effort.trim() === '' ? null : Number(effort.replace(',', '.'));
                setEffort(null);
                if (n === (verdier.effort ?? null) || (n !== null && Number.isNaN(n))) return;
                lagreTekst({ effort: n });
              }}
            />
            <span className="mu-hint">ukesverk</span>
          </label>
          <p className="mu-hint mu-tall-hint">{`Ingen sum. ${navnDecider} ser tallene når han bekrefter; veggen viser dem aldri.`}</p>
        </div>
      ) : null}
    </div>
  );
}

/** Én faktor som segmenter: ett tabstopp, piltastene flytter, valget med hake (DD-56). */
function Segmenter({ navn, verdier, vis, aktiv, onVelg }: { navn: string; verdier: number[]; vis: (v: number) => string; aktiv: number | undefined; onVelg: (v: number) => void }) {
  const rot = useRef<HTMLDivElement>(null);
  const flytt = (e: KeyboardEvent<HTMLDivElement>) => {
    const fram = e.key === 'ArrowRight' || e.key === 'ArrowDown';
    const bak = e.key === 'ArrowLeft' || e.key === 'ArrowUp';
    if (!fram && !bak) return;
    e.preventDefault();
    e.stopPropagation();
    const i = verdier.indexOf(aktiv ?? Number.NaN);
    const til = fram ? (i + 1) % verdier.length : (i - 1 + verdier.length) % verdier.length;
    onVelg(verdier[til]!);
    rot.current?.querySelectorAll<HTMLElement>('[role="radio"]')[til]?.focus();
  };
  return (
    <div className="mu-faktor-rad">
      <span className="mu-faktor" aria-hidden="true">
        {navn}
      </span>
      <div ref={rot} role="radiogroup" aria-label={navn} className="h-radio mu-segment" onKeyDown={flytt}>
        {verdier.map((v, n) => {
          const paa = aktiv === v;
          return (
            <button key={v} type="button" role="radio" aria-checked={paa} aria-label={`${navn} ${vis(v)}`} tabIndex={paa || (aktiv === undefined && n === 0) ? 0 : -1} className={paa ? 'h-radio-valg h-radio-paa' : 'h-radio-valg'} onClick={() => onVelg(v)}>
              {paa ? <Ikon navn="checkmark-outline" className="h-radio-hake" /> : null}
              {vis(v)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
